import React, { useEffect, useState } from 'react';
import { Button, Grid, Alert, Typography, IconButton, InputLabel, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CancelIcon from '@mui/icons-material/Cancel';
import PlusIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import SimpleDialog from 'components/dialogs/Dialog';
import { useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormInputNumber } from 'components/form/FormInputNumber';
import { FormInputPercentage } from 'components/form/FormInputPercentage';
import SimpleTable from 'components/table/SimpleTable';
import {
  CancellationRuleFormType,
  CancellationRuleCategoryItemFormType,
  CancellationRulesTwoCategoryFormType,
  CancellationRulesGeneralFormType,
  cancellationRulesTwoCategorySchema,
  cancellationRulesGeneralSchema,
} from 'components/template_editor/context';
import { isCancellationRulesValid } from 'semshared/offer/cancellation';
import i18next from 'i18next';
import { dispatchException, dispatchMessage } from 'helper/snackbar';
import { useDispatch } from 'react-redux';

interface CancellationRulesDialogProps {
  open: boolean;
  onCancel: () => void;
  onSave: (rules: CancellationRuleFormType[], toleranceRate: number | null) => Promise<void>;
  initialRules: CancellationRuleFormType[];
  initialToleranceRate?: number | null;
  /** When true, show Übernachtungsgäste + Seminargäste sections; when false, show single Stornofristen table. Driven by hotel policy (Übernachtungsgäste/Seminargäste von-bis). */
  hotelHasCategoryCancellation: boolean;
}

/** Convert stored fraction (0-1) to form percentage (0-100), max 2 decimal places. */
const fractionToPercent = (fraction: number) => Math.round(fraction * 10000) / 100;

/** True when any rule has overnight or seminar category fields (hotel/offer has category-specific conditions). */
export function hasCategorySpecificRules(rules: CancellationRuleFormType[]): boolean {
  return rules.some(
    r =>
      r.minOvernight != null ||
      r.maxOvernight != null ||
      r.minTotalGuests != null ||
      r.maxTotalGuests != null,
  );
}

/** Split rules into overnight and seminar for form; for display use splitRulesForDisplay. */
function splitRulesByCategory(rules: CancellationRuleFormType[]): { overnight: CancellationRuleCategoryItemFormType[]; seminar: CancellationRuleCategoryItemFormType[] } {
  const overnight: CancellationRuleCategoryItemFormType[] = [];
  const seminar: CancellationRuleCategoryItemFormType[] = [];
  for (const r of rules) {
    const hasOvernight = r.minOvernight != null || r.maxOvernight != null;
    const hasSeminar = r.minTotalGuests != null || r.maxTotalGuests != null;
    const item = { id: r.id, daysToEvent: r.daysToEvent, rate: fractionToPercent(r.rate ?? 0) };
    if (hasOvernight) overnight.push({ ...item });
    if (hasSeminar) seminar.push({ ...item });
    if (!hasOvernight && !hasSeminar) overnight.push(item);
  }
  return { overnight, seminar };
}

/** Split rules for read-only display: when hasCategorySpecificRules, use overnight/seminar; else all in general. */
export function splitRulesForDisplay(rules: CancellationRuleFormType[]): {
  hasCategories: boolean;
  overnight: CancellationRuleFormType[];
  seminar: CancellationRuleFormType[];
  general: CancellationRuleFormType[];
} {
  if (!rules?.length) {
    return { hasCategories: false, overnight: [], seminar: [], general: [] };
  }
  if (!hasCategorySpecificRules(rules)) {
    return { hasCategories: false, overnight: [], seminar: [], general: rules };
  }
  const overnight: CancellationRuleFormType[] = [];
  const seminar: CancellationRuleFormType[] = [];
  for (const r of rules) {
    const hasOvernight = r.minOvernight != null || r.maxOvernight != null;
    const hasSeminar = r.minTotalGuests != null || r.maxTotalGuests != null;
    if (hasOvernight) overnight.push(r);
    if (hasSeminar) seminar.push(r);
    if (!hasOvernight && !hasSeminar) overnight.push(r);
  }
  // If split left both empty (shouldn't happen), show all as general so we don't show nothing
  if (overnight.length === 0 && seminar.length === 0) {
    return { hasCategories: false, overnight: [], seminar: [], general: rules };
  }
  return { hasCategories: true, overnight, seminar, general: [] };
}

function generalRulesFromApi(rules: CancellationRuleFormType[]): CancellationRuleCategoryItemFormType[] {
  return rules.map(r => ({ id: r.id, daysToEvent: r.daysToEvent, rate: fractionToPercent(r.rate ?? 0) }));
}

function generalRulesToApi(rules: CancellationRuleCategoryItemFormType[]): CancellationRuleFormType[] {
  return rules.map(r => ({
    id: r.id,
    daysToEvent: r.daysToEvent,
    rate: (r.rate ?? 0) / 100,
    minOvernight: null,
    maxOvernight: null,
    minTotalGuests: null,
    maxTotalGuests: null,
  }));
}

function mergeRulesToApi(
  overnightRules: CancellationRuleCategoryItemFormType[],
  seminarRules: CancellationRuleCategoryItemFormType[],
): CancellationRuleFormType[] {
  const result: CancellationRuleFormType[] = [];
  for (const r of overnightRules) {
    result.push({
      id: r.id,
      daysToEvent: r.daysToEvent,
      rate: (r.rate ?? 0) / 100,
      minOvernight: 0,
      maxOvernight: null,
      minTotalGuests: null,
      maxTotalGuests: null,
    });
  }
  for (const r of seminarRules) {
    result.push({
      id: r.id,
      daysToEvent: r.daysToEvent,
      rate: (r.rate ?? 0) / 100,
      minOvernight: null,
      maxOvernight: null,
      minTotalGuests: 0,
      maxTotalGuests: null,
    });
  }
  return result;
}

const defaultToleranceFromProp = (v?: number | null) =>
  v !== undefined && v !== null ? v : null;

function GeneralModeContent(props: CancellationRulesDialogProps) {
  const { open, onCancel, onSave, initialRules, initialToleranceRate } = props;
  const dispatch = useDispatch();
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const defaultToleranceRate = defaultToleranceFromProp(initialToleranceRate);
  const initialGeneral = generalRulesFromApi(initialRules);

  const form = useForm<CancellationRulesGeneralFormType>({
    mode: 'all',
    resolver: yupResolver(cancellationRulesGeneralSchema) as any,
    defaultValues: {
      toleranceRate: defaultToleranceRate != null ? fractionToPercent(defaultToleranceRate) : null,
      generalRules: initialGeneral,
    },
  });

  const { fields: generalFields, remove: generalRemove, append: generalAppend } = useFieldArray({
    control: form.control,
    name: 'generalRules',
  });

  const validateAll = (values: CancellationRulesGeneralFormType): string | null => {
    const merged = generalRulesToApi(values.generalRules);
    if (merged.length === 0) return null;
    if (!isCancellationRulesValid(merged)) return i18next.t('offer-info-cancellation-validation-error');
    return null;
  };

  useEffect(() => {
    if (open) {
      const rules = generalRulesFromApi(initialRules);
      form.reset({
        toleranceRate: defaultToleranceRate != null ? fractionToPercent(defaultToleranceRate) : null,
        generalRules: rules.length > 0 ? rules : [{ id: null, daysToEvent: 21, rate: 100 }],
      });
    }
  }, [open, initialRules]);

  useEffect(() => {
    if (open) {
      const sub = form.watch(() => setValidationError(validateAll(form.getValues())));
      return () => sub.unsubscribe();
    }
  }, [open, form]);

  const handleSave = () => {
    const values = form.getValues();
    const err = validateAll(values);
    if (err) {
      setValidationError(err);
      return;
    }
    setIsSaving(true);
    const toleranceRate = values.toleranceRate != null ? values.toleranceRate / 100 : null;
    onSave(generalRulesToApi(values.generalRules), toleranceRate)
      .then(() => {
        setValidationError(null);
        dispatchMessage(dispatch, i18next.t('offer-info-cancellation-rules-saved'));
      })
      .catch((e) => dispatchException(dispatch, e))
      .finally(() => setIsSaving(false));
  };

  const handleCancel = () => {
    form.reset({
      toleranceRate: defaultToleranceRate != null ? fractionToPercent(defaultToleranceRate) : null,
      generalRules: initialGeneral,
    });
    onCancel();
    setValidationError(null);
  };

  return (
    <SimpleDialog
      title={i18next.t('offer-info-dialog-cancellation-title')}
      open={open}
      setOpen={() => onCancel()}
      fullWidth
      maxWidth="md"
      customActions={
        <>
          <Button variant="contained" color="secondary" onClick={handleCancel} startIcon={<CancelIcon />} disabled={isSaving}>
            {i18next.t('cancel')}
          </Button>
          <Button variant="contained" color="primary" onClick={handleSave} disabled={isSaving}>
            {i18next.t('apply')}
          </Button>
        </>
      }
    >
      <Grid container spacing={3}>
        {validationError && (
          <Grid item xs={12}>
            <Alert severity="error" sx={{ mb: 2 }}>{validationError}</Alert>
          </Grid>
        )}
        <Grid item xs={12}>
          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item><InputLabel>{i18next.t('offer-info-cancellation-agreed-waiver')}</InputLabel></Grid>
            <Grid item>
              <Tooltip title={i18next.t('offer-info-cancellation-agreed-waiver-tooltip')}>
                <InfoIcon fontSize="small" color="action" />
              </Tooltip>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormInputPercentage name="toleranceRate" control={form.control} disabled={isSaving} label="" />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <SimpleTable
            fullWidth
            headers={[
              i18next.t('hotel-cancellation-details-cancellation-days'),
              i18next.t('offer-info-cancellation-rule-rate'),
              '',
            ]}
            rows={generalFields.map((field, index) => [
              <FormInputNumber key={`g-d-${field.id}`} name={`generalRules.${index}.daysToEvent`} control={form.control} label="" required disabled={isSaving} />,
              <FormInputPercentage key={`g-r-${field.id}`} name={`generalRules.${index}.rate`} control={form.control} label="" required disabled={isSaving} />,
              <IconButton key={`g-x-${field.id}`} onClick={() => generalRemove(index)} color="error" size="small" disabled={isSaving}><DeleteIcon /></IconButton>,
            ])}
          />
          <Button variant="outlined" color="primary" startIcon={<PlusIcon />} onClick={() => generalAppend({ id: null, daysToEvent: 0, rate: 0 })} sx={{ mt: 1 }} disabled={isSaving}>
            {i18next.t('offer-info-cancellation-rule-add')}
          </Button>
        </Grid>
        {generalFields.length > 0 && (
          <Grid item xs={12}>
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">{i18next.t('offer-info-cancellation-validation-hint')}</Typography>
            </Alert>
          </Grid>
        )}
      </Grid>
    </SimpleDialog>
  );
}

function CategoryModeContent(props: CancellationRulesDialogProps) {
  const { open, onCancel, onSave, initialRules, initialToleranceRate } = props;
  const dispatch = useDispatch();
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const defaultToleranceRate = defaultToleranceFromProp(initialToleranceRate);
  const { overnight: initialOvernight, seminar: initialSeminar } = splitRulesByCategory(initialRules);

  const form = useForm<CancellationRulesTwoCategoryFormType>({
    mode: 'all',
    resolver: yupResolver(cancellationRulesTwoCategorySchema) as any,
    defaultValues: {
      toleranceRate: defaultToleranceRate != null ? fractionToPercent(defaultToleranceRate) : null,
      overnightRules: initialOvernight,
      seminarRules: initialSeminar,
    },
  });

  const { fields: overnightFields, remove: overnightRemove, append: overnightAppend } = useFieldArray({ control: form.control, name: 'overnightRules' });
  const { fields: seminarFields, remove: seminarRemove, append: seminarAppend } = useFieldArray({ control: form.control, name: 'seminarRules' });

  const validateAll = (values: CancellationRulesTwoCategoryFormType): string | null => {
    const overnightApi = values.overnightRules.map(r => ({ daysToEvent: r.daysToEvent, rate: (r.rate ?? 0) / 100 }));
    const seminarApi = values.seminarRules.map(r => ({ daysToEvent: r.daysToEvent, rate: (r.rate ?? 0) / 100 }));
    if (overnightApi.length > 0 && !isCancellationRulesValid(overnightApi)) return i18next.t('offer-info-cancellation-validation-error');
    if (seminarApi.length > 0 && !isCancellationRulesValid(seminarApi)) return i18next.t('offer-info-cancellation-validation-error');
    return null;
  };

  useEffect(() => {
    if (open) {
      const { overnight, seminar } = splitRulesByCategory(initialRules);
      form.reset({
        toleranceRate: defaultToleranceRate != null ? fractionToPercent(defaultToleranceRate) : null,
        overnightRules: overnight.length > 0 ? overnight : (seminar.length === 0 ? [{ id: null, daysToEvent: 21, rate: 100 }] : []),
        seminarRules: seminar.length > 0 ? seminar : [],
      });
    }
  }, [open, initialRules]);

  useEffect(() => {
    if (open) {
      const sub = form.watch(() => setValidationError(validateAll(form.getValues())));
      return () => sub.unsubscribe();
    }
  }, [open, form]);

  const handleSave = () => {
    const values = form.getValues();
    const err = validateAll(values);
    if (err) {
      setValidationError(err);
      return;
    }
    setIsSaving(true);
    const toleranceRate = values.toleranceRate != null ? values.toleranceRate / 100 : null;
    onSave(mergeRulesToApi(values.overnightRules, values.seminarRules), toleranceRate)
      .then(() => {
        setValidationError(null);
        dispatchMessage(dispatch, i18next.t('offer-info-cancellation-rules-saved'));
      })
      .catch((e) => dispatchException(dispatch, e))
      .finally(() => setIsSaving(false));
  };

  const handleCancel = () => {
    form.reset({
      toleranceRate: defaultToleranceRate != null ? fractionToPercent(defaultToleranceRate) : null,
      overnightRules: initialOvernight,
      seminarRules: initialSeminar,
    });
    onCancel();
    setValidationError(null);
  };

  const renderSection = (
    name: 'overnightRules' | 'seminarRules',
    fields: { id: string }[],
    remove: (i: number) => void,
    append: (v: CancellationRuleCategoryItemFormType) => void,
    label: string,
  ) => (
    <Grid item xs={12}>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>{label}</Typography>
      <SimpleTable
        fullWidth
        headers={[
          i18next.t('hotel-cancellation-details-cancellation-days'),
          i18next.t('offer-info-cancellation-rule-rate'),
          '',
        ]}
        rows={fields.map((field, index) => [
          <FormInputNumber key={`${name}-d-${field.id}`} name={`${name}.${index}.daysToEvent`} control={form.control} label="" required disabled={isSaving} />,
          <FormInputPercentage key={`${name}-r-${field.id}`} name={`${name}.${index}.rate`} control={form.control} label="" required disabled={isSaving} />,
          <IconButton key={`${name}-x-${field.id}`} onClick={() => remove(index)} color="error" size="small" disabled={isSaving}><DeleteIcon /></IconButton>,
        ])}
      />
      <Button variant="outlined" color="primary" startIcon={<PlusIcon />} onClick={() => append({ id: null, daysToEvent: 0, rate: 0 })} sx={{ mt: 1 }} disabled={isSaving}>
        {i18next.t('offer-info-cancellation-rule-add')}
      </Button>
    </Grid>
  );

  const hasAnyRules = overnightFields.length > 0 || seminarFields.length > 0;

  return (
    <SimpleDialog
      title={i18next.t('offer-info-dialog-cancellation-title')}
      open={open}
      setOpen={() => onCancel()}
      fullWidth
      maxWidth="md"
      customActions={
        <>
          <Button variant="contained" color="secondary" onClick={handleCancel} startIcon={<CancelIcon />} disabled={isSaving}>{i18next.t('cancel')}</Button>
          <Button variant="contained" color="primary" onClick={handleSave} disabled={isSaving}>{i18next.t('apply')}</Button>
        </>
      }
    >
      <Grid container spacing={3}>
        {validationError && (
          <Grid item xs={12}><Alert severity="error" sx={{ mb: 2 }}>{validationError}</Alert></Grid>
        )}
        <Grid item xs={12}>
          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item><InputLabel>{i18next.t('offer-info-cancellation-agreed-waiver')}</InputLabel></Grid>
            <Grid item>
              <Tooltip title={i18next.t('offer-info-cancellation-agreed-waiver-tooltip')}>
                <InfoIcon fontSize="small" color="action" />
              </Tooltip>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormInputPercentage name="toleranceRate" control={form.control} disabled={isSaving} label="" />
            </Grid>
          </Grid>
        </Grid>
        {renderSection('overnightRules', overnightFields, overnightRemove, () => overnightAppend({ id: null, daysToEvent: 0, rate: 0 }), i18next.t('hotel-cancellation-category-overnight'))}
        {renderSection('seminarRules', seminarFields, seminarRemove, () => seminarAppend({ id: null, daysToEvent: 0, rate: 0 }), i18next.t('hotel-cancellation-category-seminar'))}
        {hasAnyRules && (
          <Grid item xs={12}>
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">{i18next.t('offer-info-cancellation-validation-hint')}</Typography>
            </Alert>
          </Grid>
        )}
      </Grid>
    </SimpleDialog>
  );
}

export const CancellationRulesDialog: React.FC<CancellationRulesDialogProps> = (props) => {
  const { hotelHasCategoryCancellation } = props;
  return hotelHasCategoryCancellation ? <CategoryModeContent {...props} /> : <GeneralModeContent {...props} />;
};
